// WelcomeScreen.jsx — Syncca
import { useState } from "react";

const COLORS = {
  stone: "#F9F6EE", frame: "#E8E0F0",
  primary: "#C62828", primaryHover: "#B71C1C",
  // Soft terracotta alternative for the button (see note at bottom of file)
  clay: "#BD6A5A", clayHover: "#A85949",
  secondary: "#757575", secondaryH: "#616161",
  text: "#1a1a1a", muted: "#6b7280",
};

const STONE_SHADOW = `
  0 2px 4px rgba(0,0,0,0.06),
  0 6px 12px rgba(0,0,0,0.07),
  0 18px 36px -6px rgba(0,0,0,0.11),
  0 40px 80px -16px rgba(0,0,0,0.14),
  inset 0 1px 0px rgba(255,255,255,0.90)
`;

// Horseshoe logo — gap at bottom.
// scale = overall size multiplier; wFactor/hFactor make it narrower & taller
// (legs of the arches longer, less flat). preserveAspectRatio="none" lets the
// viewBox stretch to the given width/height so the non-uniform shape takes effect.
const LogoSymbol = ({ size = 34, scale = 1.5, wFactor = 0.9, hFactor = 1.2 }) => {
  const w = size * scale * wFactor;
  const h = size * (289 / 357) * scale * hFactor;
  return (
    <svg width={w} height={h} viewBox="0 0 357 289" preserveAspectRatio="none" fill="none">
      <path fill="#C62828" d="M 177.98 13.80 C 185.90 13.76 193.02 16.01 200.74 17.42 C 208.14 19.43 215.55 22.00 222.24 25.77 C 241.68 35.95 258.08 52.59 270.53 70.47 C 289.26 96.82 301.12 126.36 309.31 157.49 C 311.25 165.56 313.46 173.52 314.53 181.78 C 316.20 189.97 317.68 198.14 318.27 206.49 C 318.90 215.83 320.96 224.65 320.99 234.00 C 321.03 240.76 320.99 247.52 321.00 254.29 C 310.69 247.14 300.61 239.65 290.31 232.49 C 289.24 222.32 288.03 212.18 286.84 202.03 C 286.10 193.54 283.67 185.38 282.47 176.94 C 279.31 164.12 276.28 151.06 271.41 138.76 C 264.67 119.21 255.01 100.82 242.93 84.04 C 235.97 74.73 227.91 66.35 218.62 59.35 C 212.47 54.88 205.53 51.42 198.53 48.52 C 187.82 44.26 176.29 43.95 165.08 46.17 C 151.65 49.07 138.95 56.77 128.59 65.62 C 121.81 71.41 116.61 78.23 111.28 85.30 C 90.71 113.36 79.04 146.42 71.96 180.21 C 69.75 188.60 69.32 197.01 67.72 205.47 C 66.08 213.13 66.23 221.20 65.31 229.03 C 65.08 230.30 65.16 232.14 64.11 233.04 C 60.76 236.39 56.38 238.66 52.75 241.75 C 49.42 244.64 45.59 246.30 42.67 249.64 C 40.52 252.23 37.38 252.96 34.37 254.28 C 34.48 244.24 33.80 233.88 34.98 224.00 C 35.77 217.61 35.43 211.35 36.72 205.02 C 38.57 196.30 38.88 187.44 40.89 178.76 C 42.62 171.77 43.53 164.66 45.69 157.77 C 48.50 146.06 52.47 134.79 56.45 123.43 C 64.44 104.11 73.94 84.28 86.44 67.45 C 97.38 52.83 110.77 38.97 126.42 29.40 C 131.35 26.00 136.88 23.46 142.49 21.40 C 147.52 19.95 151.78 17.54 156.99 16.86 C 164.21 15.83 170.56 13.41 177.98 13.80 Z"/>
      <path fill="#757575" d="M 193.03 66.99 C 205.13 70.58 216.03 78.27 224.55 87.46 C 238.86 102.30 248.21 121.75 254.99 141.02 C 260.21 154.98 263.19 169.22 266.15 183.77 C 267.29 190.58 267.58 197.44 268.67 204.26 C 269.42 208.93 270.07 213.60 270.50 218.32 C 261.09 211.52 251.93 204.41 242.60 197.52 C 241.76 192.57 240.51 187.70 239.60 182.77 C 236.79 165.83 231.43 149.26 224.37 133.64 C 219.07 121.94 212.13 111.10 202.77 102.23 C 196.23 95.56 187.23 91.36 178.05 90.04 C 169.59 90.69 161.21 94.20 154.81 99.78 C 144.30 108.76 136.33 121.10 130.57 133.56 C 122.02 152.08 116.94 171.78 113.54 191.84 C 113.08 194.68 111.25 196.43 109.10 198.13 C 101.01 204.47 92.70 210.60 84.88 217.27 C 86.09 207.71 86.15 198.17 88.40 188.75 C 89.55 178.72 91.88 168.97 94.30 159.19 C 100.48 137.46 108.07 116.88 121.40 98.42 C 125.69 92.61 130.41 86.60 136.01 82.02 C 142.16 77.05 147.95 72.31 155.42 69.41 C 167.23 64.14 180.66 62.87 193.03 66.99 Z"/>
    </svg>
  );
};

export default function WelcomeScreen({ userEmail = "", onEnter, onLogout }) {
  const [hovered, setHovered] = useState(false);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Alef:wght@400;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { height: 100%; }
        @keyframes stoneRise {
          from { opacity: 0; transform: translateY(28px) scale(0.982); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .welcome-slab { animation: stoneRise 0.7s cubic-bezier(0.22,1,0.36,1) both; }
        .wr { opacity: 0; animation: fadeUp 0.5s ease both; }
        .wr:nth-child(1) { animation-delay: 0.20s; }
        .wr:nth-child(2) { animation-delay: 0.34s; }
        .wr:nth-child(3) { animation-delay: 0.50s; }
      `}</style>

      <div style={{
        minHeight: "100dvh", height: "100dvh", background: COLORS.frame,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "10px", fontFamily: "'Alef', sans-serif",
      }}>
        <div className="welcome-slab" style={{
          background: COLORS.stone, borderRadius: "32px", boxShadow: STONE_SHADOW,
          width: "100%", maxWidth: "400px",
          height: "calc(100dvh - 20px)", maxHeight: "880px",
          direction: "ltr",
          display: "flex", flexDirection: "column",
          alignItems: "stretch", justifyContent: "space-between",
          padding: "clamp(28px,5vh,56px) clamp(28px,7vw,44px) clamp(36px,6vh,52px)",
          overflow: "hidden",
        }}>

          {/* TOP: small header lockup — logo + wordmark, aligned left */}
          <div className="wr" style={{
            display: "flex", alignItems: "center", gap: "9px",
          }}>
            <LogoSymbol size={34} />
            <span style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "1.7rem", fontWeight: 700, color: COLORS.primary,
              letterSpacing: "-0.01em", lineHeight: 1,
            }}>Syncca</span>
          </div>

          {/* MIDDLE: greeting + scope clustered near the top (sketch ②),
              a generous gap, then the question + button lower down. */}
          <div className="wr" style={{
            flex: 1,
            display: "flex", flexDirection: "column",
            justifyContent: "flex-start", alignItems: "center",
            width: "100%",
            padding: "clamp(22px,5vh,44px) 0 0",
          }}>
            {/* TOP CLUSTER — greeting (zone 1) + scope & language (zone 2) */}
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: "clamp(18px,3.6vh,30px)", width: "100%",
            }}>
              {/* ZONE 1 — intro */}
              <div style={{
                direction: "ltr", textAlign: "center",
                fontFamily: "'Alef', sans-serif",
                fontSize: "clamp(1.08rem,3.3vw,1.3rem)",
                fontWeight: 700, color: COLORS.text, lineHeight: 1.45,
                maxWidth: "330px",
              }}>
                Hi, I'm Syncca — an{" "}
                <span style={{ whiteSpace: "nowrap" }}>AI assistant</span>{" "}
                for relationships &amp; communication 🌿
              </div>

              {/* ZONE 2 — scope + language note, tightly grouped together */}
              <div style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                gap: "clamp(6px,1.4vh,10px)",
              }}>
                <div style={{
                  direction: "ltr", textAlign: "center",
                  fontFamily: "'Alef', sans-serif",
                  fontSize: "clamp(1.02rem,2.8vw,1.16rem)",
                  fontWeight: 400, color: COLORS.text, opacity: 0.82,
                  lineHeight: 1.7, maxWidth: "335px",
                }}>
                  Here to help with partners, family, and the connections that matter most.
                </div>
                <div style={{
                  direction: "ltr", textAlign: "center",
                  fontFamily: "'Alef', sans-serif",
                  fontSize: "clamp(0.88rem,2.3vw,1rem)",
                  fontWeight: 400, color: COLORS.muted, opacity: 0.9,
                  lineHeight: 1.5, maxWidth: "310px",
                }}>
                  Write in any language you like.
                </div>
              </div>
            </div>

            {/* Flexible gap above the button block */}
            <div style={{ flex: 1.35 }} />

            {/* ZONE 3 — question + button, close together */}
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: "clamp(12px,2.4vh,18px)", width: "100%",
            }}>
              <div style={{
                direction: "ltr", textAlign: "center",
                fontFamily: "'Alef', sans-serif",
                fontSize: "clamp(1.02rem,2.8vw,1.18rem)",
                fontWeight: 700, color: COLORS.secondary, lineHeight: 1.4,
              }}>
                What's on your mind today?
              </div>

              {/* Filled button — soft warm gray (lighter than the logo) with red text */}
              <button
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={onEnter}
                style={{
                  background: hovered ? "#AEA69C" : "#BDB6AB",
                  color: COLORS.primary,
                  border: "none",
                  borderRadius: "12px",
                  fontFamily: "'Alef', sans-serif",
                  fontSize: "1.15rem", fontWeight: 700,
                  height: "50px", width: "78%", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
                  boxShadow: hovered
                    ? "0 8px 24px rgba(0,0,0,0.16)"
                    : "0 4px 14px rgba(0,0,0,0.10)",
                  transform: hovered ? "translateY(-2px)" : "translateY(0)",
                  transition: "all 0.18s ease",
                }}>
                <span>Enter Syncca</span>
              </button>
            </div>

            {/* Smaller gap below the button so it sits ~65% down, not glued to footer */}
            <div style={{ flex: 0.65 }} />
          </div>

          {/* FOOTER: logout + privacy, pinned separately at the bottom */}
          <div className="wr" style={{
            display: "flex", flexDirection: "column",
            alignItems: "center", gap: "10px", width: "100%",
          }}>
            {userEmail && (
              <div style={{
                fontFamily: "'Alef', sans-serif", fontSize: "0.82rem",
                color: COLORS.muted, direction: "rtl", textAlign: "center",
              }}>
                <span>התנתקות מ-</span>
                <span style={{ color: COLORS.secondary, fontWeight: 600 }}>{userEmail}</span>
                {onLogout && <>
                  {" · "}
                  <button onClick={onLogout} style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: COLORS.muted, fontSize: "0.73rem",
                    textDecoration: "underline", fontFamily: "'Alef', sans-serif",
                  }}>יציאה</button>
                </>}
              </div>
            )}

            <div style={{
              display: "flex", alignItems: "center", gap: "8px",
              fontFamily: "'Alef', sans-serif", fontSize: "0.6rem", fontWeight: 500,
              letterSpacing: "0.13em", textTransform: "uppercase", color: COLORS.primary,
            }}>
              <span>SECURE &amp; PRIVATE</span>
              <span style={{ width: "3px", height: "3px", borderRadius: "50%", background: COLORS.primary, opacity: 0.45 }} />
              <span>BETA PHASE</span>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

/*
  ────────────────────────────────────────────────────────────
  BUTTON — tune the fill by eye. Change only the <button> style:

  CURRENT: filled warm gray (#BDB6AB, lighter than the logo gray),
  red text (#C62828). Hover darkens the gray slightly and lifts.

  • Want it lighter / more neutral? Try background #C9C3B9 (hover #BAB3A8).
  • Want warmer (closer to the cream)? Try #C4BBAC (hover #B5AC9C).
  • Prefer terracotta instead of gray? background #BD6A5A, color "#fff".
  ────────────────────────────────────────────────────────────
*/
